declare global {
  interface Window {
    Capacitor?: any;
    androidBridge?: {
      postMessage: (message: string) => void;
    };
  }
}

export type SunmiAlign = 'LEFT' | 'CENTER' | 'RIGHT';

export interface SunmiPrintTextOptions {
  text: string;
  fontSize?: number;
}

export type SunmiReceiptLine =
  | {
      type: 'text';
      content: string;
      size?: number;
      bold?: boolean;
      align?: SunmiAlign;
    }
  | {
      type: 'row';
      columns: Array<{
        content: string;
        weight?: number;
        size?: number;
        bold?: boolean;
        align?: SunmiAlign;
      }>;
    }
  | {
      type: 'divider';
    }
  | {
      type: 'space';
      height?: number;
    }
  | {
      type: 'image';
      assetName: string;
      width?: number;
      align?: SunmiAlign;
    };

export interface SunmiPrintReceiptOptions {
  lines: SunmiReceiptLine[];
}

export interface SunmiPrinterPlugin {
  init(): Promise<void>;
  printText(options: SunmiPrintTextOptions): Promise<void>;

  /**
   * Optional while some tablets still run Waitron Connect v1.
   * v1 supports printText only.
   * v2 supports printReceipt for styled rows/images.
   */
  printReceipt?: (options: SunmiPrintReceiptOptions) => Promise<void>;
}

function cap(): any {
  return window.Capacitor;
}

function nativePromise(pluginId: string, methodName: string, options: any) {
  const c = cap();
  if (!c || typeof c.nativePromise !== 'function') {
    throw new Error('Capacitor bridge not ready');
  }

  return c.nativePromise(pluginId, methodName, options || {});
}

export const SunmiPrinter: SunmiPrinterPlugin = {
  init: () => nativePromise('SunmiPrinter', 'init', {}),
  printText: (options) => nativePromise('SunmiPrinter', 'printText', options),
  printReceipt: (options) =>
    nativePromise('SunmiPrinter', 'printReceipt', options),
};

/**
 * TODO: Remove legacy printText fallback once all Waitron Connect tablets
 * have been updated to a version that supports SunmiPrinter.printReceipt().
 *
 * Current reason:
 * - Older tablet builds only support printText({ text })
 * - New receipt flow uses printReceipt({ lines }) for styled rows/images
 *
 * Safe to remove when:
 * - All active restaurant tablets are confirmed updated
 * - No production tablets are running the old native plugin
 *
 * When removing:
 * - Delete printReceiptWithFallback()
 * - Make printReceipt required again in SunmiPrinterPlugin
 * - Call SunmiPrinter.printReceipt({ lines }) directly from receipt screens
 */
export async function printReceiptWithFallback(options: {
  lines: SunmiReceiptLine[];
  fallbackText: string;
  fontSize?: number;
}): Promise<void> {
  try {
    if (typeof SunmiPrinter.printReceipt !== 'function') {
      throw new Error('printReceipt not available');
    }

    await SunmiPrinter.printReceipt({
      lines: options.lines,
    });

    return;
  } catch (err: any) {
    console.warn(
      '[SunmiPrinter] printReceipt failed. Falling back to legacy printText().',
      err,
    );

    await SunmiPrinter.printText({
      text: options.fallbackText,
      fontSize: options.fontSize ?? 28,
    });
  }
}

export function isRunningInWaitronConnect(): boolean {
  const c = cap();

  return (
    !!c &&
    typeof c.nativePromise === 'function' &&
    typeof window.androidBridge?.postMessage === 'function'
  );
}
